import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pctTexto, ROTULO_DESTINACAO_VENDA, ROTULO_TRATAMENTO, TRATAMENTOS, type DestinacaoVenda, type Tratamento } from '@via-tax/contracts'
import { Aviso, Botao, Entrada, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { EntradaNumero } from '../components/EntradaNumero'
import { mesCurto, reais } from '../formatos'
import type { ContextoSessao, ListaReceitas } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Receita sem nota fiscal (ADR 0034). Nasceu das construtoras: a venda de unidade é documentada por
 * contrato e escritura, não por NF-e — sem este lançamento a simulação enxerga só parte da empresa.
 *
 * A tela responde à pergunta que motivou o pedido: o que essa receita paga hoje de PIS/COFINS, e o que
 * ela pagaria de IBS/CBS em 2027.
 */

const TRATAMENTOS_UTEIS: Tratamento[] = ['INTEGRAL', 'REDUCAO_50', 'REDUCAO_70', 'REDUCAO_60', 'REDUCAO_30', 'ALIQUOTA_ZERO', 'ISENCAO', 'IMUNIDADE']
const DESTINACOES: DestinacaoVenda[] = ['B2C', 'B2B_COM_CREDITO', 'B2B_SEM_CREDITO']

export function ReceitasSemNota({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const pode = (contexto.capacidades as string[]).includes('editar_empresa')
  const [erroForm, setErroForm] = useState<string | null>(null)
  const [f, setF] = useState({
    competencia: '',
    descricao: '',
    valor: 0,
    pis: 0,
    cofins: 0,
    iss: 0,
    icms: 0,
    tratamento: 'REDUCAO_50' as Tratamento,
    destinacao: 'B2C' as DestinacaoVenda,
  })

  const lista = useQuery(trpc.receitaSemNota.listar.queryOptions({}))
  const competencias = useQuery(trpc.receitaSemNota.competencias.queryOptions())
  const atualizar = { onSuccess: () => qc.invalidateQueries({ queryKey: trpc.receitaSemNota.pathKey() }) }
  const criar = useMutation(
    trpc.receitaSemNota.criar.mutationOptions({
      onSuccess: () => {
        atualizar.onSuccess()
        setF((x) => ({ ...x, descricao: '', valor: 0, pis: 0, cofins: 0, iss: 0, icms: 0 }))
        setErroForm(null)
      },
    }),
  )
  const excluir = useMutation(trpc.receitaSemNota.excluir.mutationOptions(atualizar))
  return (
    <PainelReceitas
      pode={pode}
      f={f}
      setF={setF}
      d={lista.data}
      competencias={competencias.data ?? []}
      erroForm={erroForm}
      setErroForm={setErroForm}
      salvando={criar.isPending}
      erroSalvar={criar.isError ? criar.error : null}
      salvar={(v) => criar.mutate(v)}
      excluir={(id) => excluir.mutate({ id })}
      excluindo={excluir.isPending}
      erroLista={lista.isError ? lista.error : null}
      erroExcluir={excluir.isError ? excluir.error : null}
    />
  )
}

type Form = {
  competencia: string
  descricao: string
  valor: number
  pis: number
  cofins: number
  iss: number
  icms: number
  tratamento: Tratamento
  destinacao: DestinacaoVenda
}

/** Só a aparência: recebe os dados prontos, para a tela e para a conferência visual. */
export function PainelReceitas({
  pode,
  f,
  setF,
  d,
  competencias,
  erroForm,
  setErroForm,
  salvando,
  erroSalvar,
  salvar,
  excluir,
  excluindo,
  erroLista,
  erroExcluir,
}: {
  pode: boolean
  f: Form
  setF: (fn: (x: Form) => Form) => void
  d: ListaReceitas | undefined
  competencias: string[]
  erroForm: string | null
  setErroForm: (v: string | null) => void
  salvando: boolean
  erroSalvar: unknown
  salvar: (v: Form) => void
  excluir: (id: string) => void
  excluindo: boolean
  erroLista: unknown
  erroExcluir: unknown
}) {

  const enviar = () => {
    if (!f.competencia) return setErroForm('Escolha a competência.')
    if (f.descricao.trim().length < 3) return setErroForm('Descreva a receita (de onde ela veio).')
    if (f.valor <= 0) return setErroForm('Informe o valor da receita.')
    setErroForm(null)
    salvar(f)
  }

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Receitas sem nota fiscal</h1>
        <p className="mt-1 max-w-3xl text-sm text-tinta-2">
          Receita que não passa por NF-e nem NFS-e — a venda de unidade imobiliária, documentada por contrato e escritura, é o caso clássico. Sem lançar aqui, a simulação enxerga
          só parte da empresa: o que essa receita paga hoje de PIS/COFINS não aparece para ser comparado com o IBS/CBS de 2027.
        </p>
      </header>

      {d && d.linhas.length > 0 && (
        <section className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-linha bg-painel p-4">
            <p className="text-[11px] text-tinta-3">Receita lançada</p>
            <p className="num text-xl font-semibold">{reais(d.total)}</p>
            <p className="text-[11px] text-tinta-3">{d.linhas.length} lançamento(s)</p>
          </div>
          <div className="rounded-lg border border-linha bg-painel p-4">
            <p className="text-[11px] text-tinta-3">PIS/COFINS pago hoje</p>
            <p className="num text-xl font-semibold">{reais(d.pisCofins)}</p>
            <p className="text-[11px] text-tinta-3">informado nos lançamentos</p>
          </div>
          <div className="rounded-lg border border-linha bg-painel p-4">
            <p className="text-[11px] text-tinta-3">IBS/CBS em {d.ano}</p>
            <p className="num text-xl font-semibold">{reais(d.ibsCbs)}</p>
            <p className="text-[11px] text-tinta-3">
              {pctTexto(d.aliquotas.cbs + d.aliquotas.ibs)} sobre a base{d.aliquotas.status === 'ESTIMATIVA' ? ', ainda estimada' : ''}
            </p>
          </div>
          <div className={`rounded-lg p-4 text-white ${d.diferenca > 0 ? 'bg-alerta' : 'bg-navy'}`}>
            <p className="text-[11px] text-white/75">Diferença</p>
            <p className="num text-xl font-semibold">
              {d.diferenca >= 0 ? '+' : '−'}
              {reais(Math.abs(d.diferenca))}
            </p>
            <p className="text-[11px] text-white/75">{d.diferenca > 0 ? 'o imposto novo é maior' : 'o imposto novo é menor'}</p>
          </div>
        </section>
      )}

      {pode && (
        <section className="flex flex-col gap-4 rounded-lg border border-linha bg-painel p-5">
          <h2 className="text-sm font-semibold">Lançar receita</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">Competência</span>
              <select value={f.competencia} onChange={(e) => setF((x) => ({ ...x, competencia: e.target.value }))} className="w-full min-w-0 rounded-md border border-linha bg-painel px-3 py-2 text-sm">
                <option value="">escolha…</option>
                {competencias.map((c) => (
                  <option key={c} value={c}>
                    {mesCurto(c)}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-tinta-3">só os meses que a simulação olha</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px] sm:col-span-2">
              <span className="font-medium">De onde veio</span>
              <Entrada value={f.descricao} onChange={(e) => setF((x) => ({ ...x, descricao: e.target.value }))} placeholder="Unidade 302 — Residencial Aurora" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">Valor da receita</span>
              <EntradaNumero valor={f.valor} onValor={(v) => setF((x) => ({ ...x, valor: v ?? 0 }))} tipo="reais" aria-label="Valor da receita" />
            </label>

            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">PIS pago</span>
              <EntradaNumero valor={f.pis} onValor={(v) => setF((x) => ({ ...x, pis: v ?? 0 }))} tipo="reais" aria-label="PIS pago" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">COFINS pago</span>
              <EntradaNumero valor={f.cofins} onValor={(v) => setF((x) => ({ ...x, cofins: v ?? 0 }))} tipo="reais" aria-label="COFINS pago" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">ISS pago</span>
              <EntradaNumero valor={f.iss} onValor={(v) => setF((x) => ({ ...x, iss: v ?? 0 }))} tipo="reais" aria-label="ISS pago" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">ICMS pago</span>
              <EntradaNumero valor={f.icms} onValor={(v) => setF((x) => ({ ...x, icms: v ?? 0 }))} tipo="reais" aria-label="ICMS pago" />
            </label>

            <label className="flex min-w-0 flex-col gap-1 text-[13px] sm:col-span-2">
              <span className="font-medium">Tratamento em 2027</span>
              <select
                value={f.tratamento}
                onChange={(e) => setF((x) => ({ ...x, tratamento: e.target.value as Tratamento }))}
                className="w-full min-w-0 rounded-md border border-linha bg-painel px-3 py-2 text-sm"
              >
                {TRATAMENTOS_UTEIS.filter((t) => TRATAMENTOS.includes(t)).map((t) => (
                  <option key={t} value={t}>
                    {ROTULO_TRATAMENTO[t]}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-tinta-3">venda de imóvel tem alíquota reduzida em 50%; locação, em 70% (LC 214, art. 261)</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px] sm:col-span-2">
              <span className="font-medium">Para quem foi</span>
              <select
                value={f.destinacao}
                onChange={(e) => setF((x) => ({ ...x, destinacao: e.target.value as DestinacaoVenda }))}
                className="w-full min-w-0 rounded-md border border-linha bg-painel px-3 py-2 text-sm"
              >
                {DESTINACOES.map((x) => (
                  <option key={x} value={x}>
                    {ROTULO_DESTINACAO_VENDA[x]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {erroForm && <Aviso>{erroForm}</Aviso>}
          {erroSalvar ? <Aviso>{mensagemDeErro(erroSalvar)}</Aviso> : null}
          <div>
            <Botao variante="primario" onClick={enviar} disabled={salvando}>
              Lançar
            </Botao>
          </div>
        </section>
      )}

      {d && d.linhas.length > 0 && (
        <section className="rounded-lg border border-linha bg-painel">
          <p className="border-b border-linha px-5 py-3 text-sm font-semibold">Lançamentos</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-[13px]">
              <thead>
                <tr className="border-b border-linha text-left text-[11px] text-tinta-3">
                  <th className="px-5 py-2 font-medium">Mês</th>
                  <th className="py-2 font-medium">De onde veio</th>
                  <th className="py-2 pl-4 text-right font-medium">Receita</th>
                  <th className="py-2 pl-4 text-right font-medium">PIS/COFINS hoje</th>
                  <th className="py-2 pl-4 text-right font-medium">IBS/CBS em {d.ano}</th>
                  <th className="py-2 pl-4 font-medium">Tratamento</th>
                  <th className="py-2 pl-4" />
                </tr>
              </thead>
              <tbody>
                {d.linhas.map((l) => (
                  <tr key={l.id} className="border-b border-linha last:border-0">
                    <td className="px-5 py-2">{mesCurto(l.competencia)}</td>
                    <td className="py-2">
                      {l.descricao}
                      <span className="block text-[11px] text-tinta-3">{ROTULO_DESTINACAO_VENDA[l.destinacao as DestinacaoVenda]}</span>
                    </td>
                    <td className="num whitespace-nowrap py-2 pl-4 text-right">{reais(l.valor)}</td>
                    <td className="num whitespace-nowrap py-2 pl-4 text-right">{reais(l.pis + l.cofins)}</td>
                    <td className="num whitespace-nowrap py-2 pl-4 text-right font-medium">{reais(l.ibsCbs)}</td>
                    <td className="py-2 pl-4">
                      <Selo tom={l.tratamento === 'INTEGRAL' ? 'neutro' : 'ok'}>{ROTULO_TRATAMENTO[l.tratamento as Tratamento]}</Selo>
                    </td>
                    <td className="py-2 pl-4 pr-5 text-right">
                      {pode && (
                        <Botao variante="fantasma" onClick={() => excluir(l.id)} disabled={excluindo}>
                          Excluir
                        </Botao>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {d && !d.linhas.length && <Aviso tom="info">Nenhuma receita sem nota lançada. Enquanto não houver, a simulação considera só o que veio das notas fiscais.</Aviso>}
      {erroLista ? <Aviso>{mensagemDeErro(erroLista)}</Aviso> : null}
      {erroExcluir ? <Aviso>{mensagemDeErro(erroExcluir)}</Aviso> : null}
    </div>
  )
}
